import { useTimelineContext } from "@/machines/context/timeline-context";
import { useGetEventById } from "@/machines/api/queries/events.queries.ts";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { Progress } from "@/ui/components/ui/progress";
import { Button } from "@/ui/components/ui/button";
import { Loader2, Download, Maximize } from "lucide-react";
import { toast } from "react-hot-toast";
import { AlertImage } from "@/alerts/types/machine-alert";
import { labelConfidencePercent } from "@/shared/utils/confidence";

interface ImagePreviewProps {
  images?: AlertImage[];
  /** Show ONLY the given alert images — never fall back to the timeline event's frames. */
  alertOnly?: boolean;
  /** External loading (e.g. the alert-detail fetch) — shows the loading state. */
  isLoading?: boolean;
}

const ImagePreview = ({ images: propImages, alertOnly = false, isLoading = false }: ImagePreviewProps = {}) => {
  const { t } = useTranslation("machines");
  const { currentEvents } = useTimelineContext();
  const eventWithImages = Object.values(currentEvents?.layers || {}).find(e => e.hasImages);
  const eventId = eventWithImages?.id;


  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDataChanging, setIsDataChanging] = useState(false);

  const { data, status } = useGetEventById(eventId);

  // Use prop images if provided, otherwise fall back to entity images
  // (unless alertOnly — then the alert's own images are the only source).
  const entityImages = data?.data?.imageUrls || [];
  const alertImages = propImages?.map(img => img.signed_url) || [];
  const isUsingPropImages = alertImages.length > 0;
  const images = isUsingPropImages ? alertImages : alertOnly ? [] : entityImages;

  // Default to the alert frame — the highest-confidence image.
  const alertFrameIndex = useMemo(() => {
    if (!propImages?.length) return 0;
    let best = 0;
    for (let i = 1; i < propImages.length; i++) {
      if ((labelConfidencePercent(propImages[i].labels) ?? -1) > (labelConfidencePercent(propImages[best].labels) ?? -1)) best = i;
    }
    return best;
  }, [propImages]);

  // Reset images when data is changing
  useEffect(() => {
    setIsDataChanging(true);
    const timeout = setTimeout(() => {
      setIsDataChanging(false);
    }, 1000); // Show loading for 1 second when data changes
    return () => clearTimeout(timeout);
  }, [eventId]); // Reset when eventId changes (indicates new data)

  const containerRef = useRef<HTMLDivElement>(null);

  const nextImage = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const handleDownload = () => {
    const currentImage = images[currentIndex];
    if (!currentImage) {
      toast.error(t("imagePreview.toast.noImage"));
      return;
    }

    // Open image in new tab since Google Cloud Storage blocks direct download
    window.open(currentImage, '_blank');
    toast.success(t("imagePreview.toast.openedInNewTab"));
  };

  const handleFullscreen = () => {
    if (!containerRef.current) return;

    if (containerRef.current.requestFullscreen) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err);
        toast.error(t("imagePreview.toast.fullscreenFailed"));
      });
    } else {
      toast.error(t("imagePreview.toast.fullscreenUnsupported"));
    }
  };

  useEffect(() => {
    // Reset index if images change — alert images open on the alert frame.
    setCurrentIndex(isUsingPropImages ? alertFrameIndex : 0);
  }, [eventId, isUsingPropImages, alertFrameIndex]);

  if (!alertOnly && !currentEvents && !isUsingPropImages) {
    return <div className="text-center text-sm text-muted-foreground">{t("imagePreview.noSelection")}</div>;
  }

  if (!alertOnly && !eventId && !isUsingPropImages) {
    return <></>;
  }

  if (isLoading || ((status === "pending" || isDataChanging) && !isUsingPropImages && !alertOnly)) {
    return (
      <div className="w-full max-w-md mx-auto p-4">
        <div className="relative overflow-hidden rounded-lg bg-background border h-[300px] flex flex-col items-center justify-center space-y-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <div className="text-center space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              {isDataChanging ? t("imagePreview.updating") : t("imagePreview.loading")}
            </p>
            <div className="w-32">
              <Progress value={isDataChanging ? 40 : 60} className="h-1" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="w-full max-w-md mx-auto p-4">
        <div className="relative overflow-hidden rounded-lg bg-background border h-[300px] flex items-center justify-center">
          <div className="text-center text-sm text-muted-foreground">{t("imagePreview.empty")}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .image-preview-container:fullscreen {
          height: 100vh;
          max-width: none;
          border-radius: 0;
        }
        .image-preview-container:fullscreen .image-preview-wrapper {
          height: 100vh;
        }
        .image-preview-container:fullscreen .image-preview-img {
          max-height: 100vh;
          height: 100vh;
        }
      `}</style>
      <div className="w-full max-w-md mx-auto p-4">
        <div className="image-preview-container relative overflow-hidden rounded-lg bg-background border h-[300px]" ref={containerRef}>
          <div
            className="image-preview-wrapper flex transition-transform duration-300 ease-in-out bg-black/95 dark:bg-black h-full"
            style={{
              width: `${images.length * 100}%`,
              transform: `translateX(-${currentIndex * (100 / images.length)}%)`,
            }}
          >
            {images.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={t("imagePreview.imageAlt", { index: index + 1 })}
                className="image-preview-img w-full h-full object-contain flex-shrink-0"
                style={{ width: `${100 / images.length}%` }}
              />
            ))}
          </div>

        {/* Pagination label */}
        <div className="absolute top-2 right-2 bg-black/70 dark:bg-black/80 text-white text-xs px-2 py-1 rounded">
          {t("eventDetails.imageCounter", { index: currentIndex + 1, total: images.length })}
        </div>

        {/* Per-image confidence (only alert images carry it) */}
        {isUsingPropImages && labelConfidencePercent(propImages?.[currentIndex]?.labels) != null && (
          <div className="absolute bottom-2 left-2 bg-black/70 dark:bg-black/80 text-white text-xs px-2 py-1 rounded">
            {t("eventDetails.confidence", { value: formatNumber(labelConfidencePercent(propImages?.[currentIndex]?.labels)!) })}
          </div>
        )}


        {/* Navigation buttons */}
        <button
          onClick={prevImage}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/70 dark:bg-black/80 text-white px-3 py-2 rounded hover:bg-black/80 dark:hover:bg-black transition-colors"
        >
          ‹
        </button>
        <button
          onClick={nextImage}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/70 dark:bg-black/80 text-white px-3 py-2 rounded hover:bg-black/80 dark:hover:bg-black transition-colors"
        >
          ›
        </button>

        {/* Action Controls */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleFullscreen}
            className="h-8 w-8 bg-black/70 dark:bg-black/80 hover:bg-black/80 dark:hover:bg-black text-white"
            title={t("video.fullscreen")}
          >
            <Maximize className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            className="h-8 w-8 bg-black/70 dark:bg-black/80 hover:bg-black/80 dark:hover:bg-black text-white"
            title={t("video.download")}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
    </>
  );
};

export default ImagePreview;
